import type { MotionStyle, MotionValue } from "motion/react";
import { cubicBezier, motion, useTransform } from "motion/react";

/**
 * Obtains the transform styles for a letter based on its index and the scroll progress
 * @param scrollProgress The current scroll progress
 * @param index The index of the letter
 * @param durationPerLetter The duration of the animation per letter
 * @returns The styles for the letter
 */
const getStyles = (scrollProgress: MotionValue<number>, index: number, durationPerLetter: number) => {
  // Have each letter start animating one after another based on their index
  const start = index * durationPerLetter;
  const end = start + durationPerLetter;

  const ease = cubicBezier(0, 0.55, 0.45, 1); // easeOutCirc
  return {
    y: useTransform(scrollProgress, [start, end], [0, 30], { ease }),
    opacity: useTransform(scrollProgress, [start, end], [1, 0], { ease }),
  };
};

/**
 * Applies a pull-down animation to each letter of the given text based on scroll progress
 * @param text The text to animate
 * @param length The length of the text (for overriding the text length calculation. used for cross-component animations)
 * @param className Class styles
 * @param style Styles
 * @param scrollProgress The current scroll progress
 * @param index Optional index to start from (for cross-component animations)
 * @returns The text supplied with a pull-down animation applied to each letter
 */
export default function LettersPullDown({
  text,
  length,
  className,
  style,
  scrollProgress,
  index,
}: {
  text: string;
  length?: number;
  className?: string;
  style?: MotionStyle;
  scrollProgress: MotionValue<number>;
  index?: number;
}) {
  // Calculate the total duration of each letters' animation based on the length of the text (with some slight overlap)
  const durationPerLetter = 1 / (length ?? text.length);

  return (
    <motion.div className={`flex overflow-hidden ${className ?? ""}`} style={style}>
      {text.split("").map((letter, i) => {
        // Get the transform styles for the letter
        const style = getStyles(scrollProgress, (index ?? 0) + i, durationPerLetter);
        return (
          <motion.span key={i} className='shrink-0' style={style}>
            {letter === " " ? "\u00A0" : letter}
          </motion.span>
        );
      })}
    </motion.div>
  );
}
